import { DownOutlined, FilePdfOutlined, TagsOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { statusLabel } from "@wms/domain";
import type { EntityMeta, Workflow } from "@wms/domain";
import { Button, Collapse, Dropdown, Popconfirm, Tag, Tooltip } from "antd";
import type { MenuProps } from "antd";
import type { ReactNode } from "react";
import { api } from "../api/client";
import { useAuthStore } from "../auth/store";
import type { PanelAction } from "./actionGroups";
import { groupActions, isDanger, summaryRows } from "./actionGroups";
import { AssistantDocumentButton, assistantAvailable } from "./AssistantDocumentButton";
import type { ActionRunner } from "./DocumentActions";
import { useActionRunner } from "./DocumentActions";
import { HelpHint } from "./HelpHint";
import "./actionPanel.css";

/**
 * ТЗ «Рабочие столы и персонализация» (31.10.2026), раздел 11 (UXW-47…53), приложение §7: the grouped action panel of
 * a document card (option «Панель действий документа по группам»). (1) the current step — status, «Что сейчас», one
 * instruction and ONE main button with what it changes, «Дальше»; (2) «Другие действия»; (3) later steps with their
 * reasons, folded; (4) «Документы» and «Этикетки»; (5) «ИИ-помощник»; (6) the dangerous actions apart. The same panel
 * serves the stepwise card and the full card; every button goes through the one runner of DocumentActions, so the
 * codes, rights, dialogs, previews, confirmations, request keys and the refresh of the child lists stay as they were.
 * The dialogs live at one place at the end of the panel: a regrouping or a new status never unmounts an open one.
 */

interface Props {
  entity: string;
  id: string;
  meta: EntityMeta;
  row?: Record<string, unknown>;
  workflow?: Workflow | null;
  /** «full» — the whole panel in the full card; «steps» — the step block above already says the question. */
  view: "full" | "steps";
  /** steps: the step looked at is the live one (its question and «Дальше» are already on screen). */
  viewingLive?: boolean;
  onDone: () => void;
  /** Opens a child tab of the card («Товары») — the main step of a draft without lines (UXW-48). */
  onOpenTab?: (key: string) => void;
}

const CORE_SUMMARY = new Set(["lines", "plan_total", "fact_total", "picked_total", "shipped_total", "counted", "uncounted"]);

interface PermissionItem {
  code: string;
  label?: string;
}

/** The (?) of an action: what happens, whether stock, reserve or the document change, the full name (UXW-55). */
function actionHint(item: PanelAction): ReactNode | null {
  const { action } = item;
  const parts: ReactNode[] = [];
  if (item.text !== item.full) parts.push(<div key="full">Полное название: «{item.full}».</div>);
  if (action.help) parts.push(<div key="help">{action.help}</div>);
  if (action.outcome && action.outcome !== action.help) parts.push(<div key="outcome">Что изменится: {action.outcome}</div>);
  return parts.length ? <>{parts}</> : null;
}

function ActionButton({ item, runner, primary }: { item: PanelAction; runner: ActionRunner; primary?: boolean }) {
  const { action } = item;
  const blocked = !!item.reason;
  const button = (
    <Button
      className={primary ? "wms-panel-primary" : undefined}
      data-testid={`panel-action-${action.code}`}
      data-primary={primary || undefined}
      type={primary ? "primary" : "default"}
      danger={isDanger(action)}
      disabled={blocked}
      loading={runner.pending(action.code)}
      aria-label={item.text !== item.full ? `${item.text}: ${item.full}` : undefined}
      onClick={() => runner.start(action)}
    >
      {item.text}
    </Button>
  );
  // the full name is the tooltip of a short button (UXW-05); a disabled one says its reason as text beside it
  const titled =
    item.text !== item.full && !blocked ? (
      <Tooltip title={item.full} mouseEnterDelay={0.4}>
        <span className="wms-panel-item">{button}</span>
      </Tooltip>
    ) : (
      button
    );
  // UXW-53: the confirmation of a simple action is the one of the row, with its words of the outcome
  const confirmable = action.confirm && action.kind === "simple" && !action.preview && !blocked;
  const hint = actionHint(item);
  return (
    <span className="wms-panel-item">
      {confirmable ? (
        <Popconfirm title={`${item.full}?`} description={action.outcome ?? action.help ?? undefined} okText="Да" cancelText="Отмена" onConfirm={() => runner.confirmRun(action)}>
          {titled}
        </Popconfirm>
      ) : (
        titled
      )}
      {hint && <HelpHint subject={item.full} text={hint} />}
    </span>
  );
}

export function ActionPanel({ entity, id, meta, row, workflow, view, viewingLive, onDone, onOpenTab }: Props) {
  const can = useAuthStore((s) => s.can);
  const option = useAuthStore((s) => s.option);
  const runner = useActionRunner({ entity, id, row, permission: meta.permission, workflow, onDone, verify: true });
  const permissions = useQuery({ queryKey: ["meta", "permissions"], queryFn: () => api.get<PermissionItem[]>("/meta/permissions"), staleTime: 300_000 });
  const tabs = (meta.children ?? []).filter((c) => (!c.option || option(c.option)) && (!c.permission || can(c.permission))).map((c) => c.entity);
  const status = String(row?.status ?? "");
  const groups = groupActions({ actions: meta.actions, status, row, workflow, allowed: runner.allowed, reasonOf: runner.blockedReason, tabs });
  const current = groups.current;
  const statusField = meta.fields.find((f) => f.name === "status" && f.type === "enum");
  const statusText = status ? (statusField?.options?.find((o) => o.value === status)?.label ?? statusLabel(status)) : null;

  /** UXW-50: «нет права» names the right and promises nothing; «позже» says what has to happen first. */
  const reasonWords = (item: PanelAction): string => {
    if (item.blocked !== "right") return item.reason ?? "";
    const code = item.action.permission ?? `${meta.permission}.write`;
    const words = permissions.data?.find((p) => p.code === code)?.label;
    return words ? `Выполняет сотрудник с правом «${words}». Следующие шаги этого не изменят — обратитесь к руководителю.` : (item.reason ?? "");
  };

  const openTab = (key: string) => {
    onOpenTab?.(key);
    // the stepwise card may already show that tab: bring it into view
    window.setTimeout(() => document.querySelector(`[data-pane="${key}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const full = view === "full";
  const primary = current?.primary ?? null;
  const showStory = full || !viewingLive;
  // the counts that tell the state of the document; the other counters only when they are not zero
  const summary = full ? summaryRows(workflow?.summary, true).filter((r) => CORE_SUMMARY.has(r.key) || (r.value !== "0" && r.value !== "")) : [];

  const currentBlock = current && (showStory || primary) && (
    <div className="wms-panel-current" data-testid="panel-current" data-tone={current.tone}>
      {full && statusText && (
        <div className="wms-panel-status">
          Статус: <b data-testid="panel-status">{statusText}</b>
        </div>
      )}
      {showStory && (
        <>
          <div className="wms-panel-kicker">
            {full ? "Что сейчас" : "Сейчас по документу"}
            <HelpHint subject="Что сейчас" text="Текущий шаг документа по данным сервера: что сделать сейчас и что изменит основная кнопка. Другие разрешённые действия — ниже, ни одно не удалено." />
          </div>
          <div className="wms-panel-title" data-testid="panel-now">
            {current.title}
          </div>
          {full && current.instruction && <div className="wms-panel-instruction">{current.instruction}</div>}
          {summary.length > 0 && (
            <div className="wms-panel-summary" data-testid="panel-summary">
              {summary.map((r) => (
                <span key={r.key}>
                  {r.label}: <b>{r.value}</b>
                </span>
              ))}
            </div>
          )}
        </>
      )}
      {primary && (
        <>
          <div className="wms-panel-row" data-testid="panel-primary">
            {primary.kind === "action" ? (
              <ActionButton item={primary.item} runner={runner} primary />
            ) : (
              <Button type="primary" className="wms-panel-primary" data-testid="panel-primary-tab" data-tab={primary.key} onClick={() => openTab(primary.key)}>
                {primary.label}
              </Button>
            )}
          </div>
          {primary.consequence && (primary.kind === "action" || !full) && (
            // UX1-006 (6) / UXW-55: what the press changes, in words, under the button — not only in a tooltip (the
            // instruction of a tab step is already said above in the full card)
            <div className="wms-panel-consequence" data-testid="panel-consequence">
              {primary.kind === "action" ? "Что изменится: " : ""}
              {primary.consequence}
            </div>
          )}
        </>
      )}
      {full && current.next && (
        <div className="wms-panel-next" data-testid="panel-next">
          Дальше: {current.next}
        </div>
      )}
    </div>
  );

  const byCode = (code: string) => groups.documents.find((x) => x.action.code === code)?.action;
  const docItems: MenuProps["items"] = [
    ...(runner.showDocs ? [{ key: "__viewer", label: "Все документы: просмотр, PDF и печать" }] : []),
    // UXW-52: every form of the card, with its full name inside the menu; the short words only on the choice
    ...groups.documents.map((item) => ({
      key: item.action.code,
      disabled: !!item.reason,
      label: (
        <span data-testid={`panel-document-${item.action.code}`}>
          {item.text !== item.full ? (
            <>
              <b>{item.text}</b> — {item.full}
            </>
          ) : (
            item.full
          )}
          {item.reason ? ` (${item.reason})` : ""}
        </span>
      ),
    })),
  ];
  const openDocument = (key: string) => {
    if (key === "__viewer") runner.openDocuments();
    else {
      const action = byCode(key);
      if (action) runner.start(action);
    }
  };
  const documentsButton =
    docItems.length === 0 ? null : docItems.length === 1 ? (
      <Button icon={<FilePdfOutlined />} data-testid="panel-documents" onClick={() => openDocument(String(docItems[0]!.key))}>
        Документы
      </Button>
    ) : (
      <Dropdown trigger={["click"]} menu={{ items: docItems, onClick: ({ key }) => openDocument(key) }}>
        <Button icon={<FilePdfOutlined />} data-testid="panel-documents">
          Документы <DownOutlined />
        </Button>
      </Dropdown>
    );
  const labelsButton =
    groups.labels.length === 0 ? null : groups.labels.length === 1 ? (
      <Button
        icon={<TagsOutlined />}
        data-testid="panel-labels"
        disabled={!!groups.labels[0].reason}
        title={groups.labels[0].full}
        onClick={() => runner.start(groups.labels[0].action)}
      >
        Этикетки
      </Button>
    ) : (
      <Dropdown
        trigger={["click"]}
        menu={{
          items: groups.labels.map((item) => ({ key: item.action.code, label: item.full, disabled: !!item.reason })),
          onClick: ({ key }) => {
            const action = groups.labels.find((x) => x.action.code === key)?.action;
            if (action) runner.start(action);
          },
        }}
      >
        <Button icon={<TagsOutlined />} data-testid="panel-labels">
          Этикетки <DownOutlined />
        </Button>
      </Dropdown>
    );
  const labelsReason = groups.labels.length === 1 ? groups.labels[0].reason : null;
  const assistant = assistantAvailable(entity, row?.number, can) ? <AssistantDocumentButton entity={entity} number={row?.number} label="ИИ-помощник" /> : null;

  return (
    <section className="wms-action-panel" data-testid="action-panel" data-view={view}>
      {currentBlock}
      {!current && full && statusText && (
        <div className="wms-panel-status" style={{ fontSize: 15 }}>
          Статус: <b data-testid="panel-status">{statusText}</b>
        </div>
      )}
      {groups.other.length > 0 && (
        <div data-testid="panel-other">
          <div className="wms-panel-group-title">
            {current ? "Другие действия" : "Действия"}
            <HelpHint
              subject="Другие действия"
              text="Действия, разрешённые сейчас вашей роли, кроме основного: прямая отгрузка, необязательные этапы, возврат в черновик. У каждого — свои права, подтверждение и окно с полями."
            />
          </div>
          <div className="wms-panel-row">
            {groups.other.map((item) => (
              <div key={item.action.code} className="wms-panel-other-item">
                <ActionButton item={item} runner={runner} />
                {/* UXW-05/55: the consequence that matters is read without the (?) — «Упаковать» says what goes in */}
                {item.action.outcome && <div className="wms-panel-caption">{item.action.outcome}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {groups.future.length > 0 && (
        <Collapse
          ghost
          className="wms-panel-future"
          data-testid="panel-future"
          items={[
            {
              key: "future",
              label: `Следующие шаги и недоступные сейчас действия (${groups.future.length})`,
              children: (
                <div>
                  {primary?.kind === "tab" && (
                    // UXW-50: the way to make the later steps possible, when the server names it
                    <Button type="link" style={{ paddingInline: 0 }} onClick={() => openTab(primary.key)} data-testid="panel-future-fix">
                      Чтобы открыть следующие шаги: {primary.label}
                    </Button>
                  )}
                  {groups.future.map((item) => (
                    <div key={item.action.code} className="wms-panel-future-item" data-testid={`panel-future-${item.action.code}`}>
                      <ActionButton item={item} runner={runner} />
                      <Tag color={item.blocked === "right" ? "orange" : "default"} data-testid={`panel-blocked-${item.action.code}`} data-blocked={item.blocked ?? undefined}>
                        {item.blocked === "right" ? "Нет права" : "Позже"}
                      </Tag>
                      <span className="wms-panel-reason">{reasonWords(item)}</span>
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}
      {(documentsButton || labelsButton || assistant || groups.assist.length > 0) && (
        <div data-testid="panel-aux">
          <div className="wms-panel-row" style={{ marginTop: 0 }}>
            {documentsButton}
            {labelsButton}
            {groups.assist.map((item) => (
              <ActionButton key={item.action.code} item={item} runner={runner} />
            ))}
            {assistant}
            <HelpHint
              subject="Документы, этикетки и помощник"
              text="«Документы» — печатные формы документа: просмотр PDF, скачивание и печать, полные названия форм — в списке. «Этикетки» — печать этикеток с выбором вида и количества. Печать не проводит складских операций и не обязательна для следующих шагов."
            />
          </div>
          {labelsReason && <div className="wms-panel-reason">Этикетки: {labelsReason}</div>}
        </div>
      )}
      {groups.danger.length > 0 && (
        <div className="wms-panel-danger" data-testid="panel-danger">
          <div className="wms-panel-group-title">Отмена и необратимые действия</div>
          {groups.danger.map((item: PanelAction) => (
            <div key={item.action.code} className="wms-panel-danger-item">
              <ActionButton item={item} runner={runner} />
              {/* UXW-53: the consequence and the reason are read before the press, not only in the confirmation */}
              <span className="wms-panel-reason">{item.reason ? reasonWords(item) : (item.action.outcome ?? item.action.help ?? "")}</span>
            </div>
          ))}
        </div>
      )}
      {runner.dialogs}
    </section>
  );
}
